import { chatTable, notificationTable, chatBucket } from '$lib/supabase';
import { supabaseClient } from '$lib/supabase';
import { validate as uuidValidate } from 'uuid';
import type { ChatAttrs } from './chat.type';
import DOMPurify from 'dompurify';
import { getUser } from '$lib/utils/user';

// import { SupabaseClient } from '@supabase/supabase-js';

const GET_COL_SET = '*'; // include all columns

/**
 * getChat - Get a single chat with the given ID
 *
 * @param <string>id - ID of post to get
 * @returns { data, error } where chat is a single supabase chat table object
 *
 * TODO: better validation of ID and return object
 *
 */
export const getChat = (id: string) => chatTable('chats').select(GET_COL_SET).eq('id', id).single();

// OUR TESTING FUNCTION
/**
 * getChatsWithNotifications - Get all chats where notifications aren't null
 *
 * @returns { data, error } where data is an array of supabase chat table objects
 *
 * TODO: better validation and error handling
 *
 */
export const getChatsWithNotifications = async () => {
	try {
		const response = await chatTable('chats').select(GET_COL_SET).not('notifications', 'is', null);

		return { data: response.data, error: null };
	} catch (error) {
		return { data: null, error: error.message || 'An error occurred while fetching chats.' };
	}
};

/**
 * getAllUserNotificationsOld - Get all chats where notifications aren't null
 *
 * @returns { data, error } where data is an array of supabase chat table objects
 *
 * TODO: better validation and error handling
 *
 */
export const getAllUserNotificationsOld = async (profileUsername: string) => {
	// console.log('memberusername in api', profileUsername, 'type', typeof profileUsername);
	try {
		const { data, error } = await supabaseClient.from('chats').select(GET_COL_SET);

		if (error) {
			console.error('An error occurred:', error);
			return false;
		}

		// Filter locally based on the notifications array
		const filteredData = data
			.filter((chat) => {
				const notifications = chat.notifications || [];
				return notifications.some((notification) => notification.mentioned === profileUsername);
			})
			.sort((a, b) => {
				// Sort by newest to oldest based on the created_at property
				const aTimestamp = new Date(a.created_at).getTime();
				const bTimestamp = new Date(b.created_at).getTime();

				return bTimestamp - aTimestamp;
			});

		return { data: filteredData, error: null };
	} catch (error) {
		return { data: null, error: error.message || 'An error occurred while fetching chats.' };
	}
};

/**
 * getAllReplyNotifications - Get all chats where notifications aren't null
 *
 * @returns { data, error } where data is an array of supabase chat table objects
 *
 * TODO: better validation and error handling
 *
 */
export const getAllReplyNotifications = async (profileUsername: string) => {
	// console.log('memberusername in api', profileUsername, 'type', typeof profileUsername);
	try {
		const { data, error } = await notificationTable('notifications')
			.select('*')
			.eq('replied_to_username', profileUsername);
		// .contains('mentioned', [profileUsername])

		if (error) {
			console.error('An error occurred:', error);
			return false;
		}
		return { data, error: null };
	} catch (error) {
		return { data: null, error: error.message || 'An error occurred while fetching chats.' };
	}
};

export const getAllMentionNotifications = async (profileUsername: string) => {
	// console.log('memberusername in api', profileUsername, 'type', typeof profileUsername);
	try {
		const { data, error } = await notificationTable('notifications')
			.select('*')
			// .eq('replied_to_username', profileUsername)
			.contains('mentioned', [profileUsername]);

		if (error) {
			console.error('An error occurred:', error);
			return false;
		}
		return { data, error: null };
	} catch (error) {
		return { data: null, error: error.message || 'An error occurred while fetching chats.' };
	}
};

/**
 * getUnreadUserNotifications - Get all chats where notifications aren't null
 *
 * @returns { data, error } where data is an array of supabase chat table objects
 *
 * TODO: better validation and error handling
 *
 */
export const getUnreadUserNotifications = async (profileUsername: string) => {
	// console.log('memberusername in api', profileUsername, 'type', typeof profileUsername);
	try {
		const { data, error } = await supabaseClient.from('chats').select(GET_COL_SET);

		if (error) {
			console.error('An error occurred:', error);
			return false;
		}

		// Filter locally based on the notifications array
		const filteredData = data.filter((chat) => {
			const notifications = chat.notifications || [];
			return notifications.some(
				(notification) => notification.mentioned === profileUsername && notification.read === false
			);
		}).length;
		// .filter((chat) => {
		// 	// Only include chats where read is false
		// 	return chat.profileUsername === profileUsername && chat.read === false;
		// });

		return { data: filteredData, error: null };
	} catch (error) {
		return { data: null, error: error.message || 'An error occurred while fetching chats.' };
	}
};

export const getAllUnreadReplyNotifications = async (profileUsername: string) => {
	// console.log('memberusername in api', profileUsername);
	try {
		const { data, error } = await notificationTable('notifications')
			.select('id')
			.eq('replied_to_username', profileUsername)
			.eq('read', false);
		// .contains('mentioned', [profileUsername])

		if (error) {
			console.error('An error occurred:', error);
			return false;
		}
		// console.log('mentioned', data);
		return { data, error: null };
	} catch (error) {
		return { data: null, error: error.message || 'An error occurred while fetching chats.' };
	}
};

export const getAllUnreadMentionNotifications = async (profileUsername: string) => {
	// console.log('memberusername in api', profileUsername);
	// console.log('memberusername in api', profileUsername, 'type', typeof profileUsername);
	try {
		const { data, error } = await notificationTable('notifications')
			.select('id')
			// .eq('replied_to_username', profileUsername)
			.contains('mentioned', [profileUsername])
			.eq('read', false);

		if (error) {
			console.error('An error occurred:', error);
			return false;
		}
		// console.log('mentioned', data);
		return { data, error: null };
	} catch (error) {
		return { data: null, error: error.message || 'An error occurred while fetching chats.' };
	}
};

/**
 * markNotificationAsRead - Mark a specific notification as read
 *
 * @param {string} id - ID of the chat to update
 * @param {string} activeUser - The username of the active user
 * @returns { data, error } where data is the updated supabase chat table object
 *
 * TODO: better validation of ID and return object
 *
 */
export const markNotificationAsRead = async (id: string) => {
	try {
		const { data: chat, error } = await supabaseClient
			.from('notifications')
			.update({ read: true })
			.eq('id', id)
			.single();

		if (error) {
			console.error('An error occurred:', error);
			return false;
		}

		if (!chat) {
			throw new Error('Chat not found');
		}
	} catch (error) {
		return { data: null, error: error.message || 'An error occurred while updating the chat.' };
	}
};

/**
 * markNotificationAsReadOld - Mark a specific notification as read
 *
 * @param {string} id - ID of the chat to update
 * @param {string} activeUser - The username of the active user
 * @returns { data, error } where data is the updated supabase chat table object
 *
 * TODO: better validation of ID and return object
 *
 */
export const markNotificationAsReadOld = async (id: string, activeUser: string) => {
	// TODO: validate that the logged in user's username is mentioned in the chat with the given id. Only they should be able to mark the notification as read.
	// const isValidUser = await isValidUserMentioned(id, activeUser);

	// if (!isValidUser) {
	// 	console.error('Profile username not mentioned in notifications.');
	// 	return false;
	// }

	try {
		const { data: chat, error } = await supabaseClient
			.from('chats')
			.select(GET_COL_SET)
			.eq('id', id)
			.single();

		if (error) {
			console.error('An error occurred:', error);
			return false;
		}

		if (!chat) {
			throw new Error('Chat not found');
		}

		// Update the chat object only if the mentioned user is the active user
		const notifications = chat.notifications || [];
		const updatedNotifications = notifications.map((notification) => {
			if (notification.mentioned === activeUser && notification.read === false) {
				return { ...notification, read: true };
			}
			return notification;
		});

		const updatedChat = await supabaseClient
			.from('chats')
			.update({ notifications: updatedNotifications })
			.eq('id', id)
			.single();

		return { data: updatedChat, error: null };
	} catch (error) {
		return { data: null, error: error.message || 'An error occurred while updating the chat.' };
	}
};

/**
 * getMessagesAll - Gets all chat messages
 *
 * @param <number>limit - no parameters
 * @returns { data, error } where data is a list of supabase chat table objects
 *
 * TODO: better validation of ID and return object
 *
 */
export const getMessagesAll = async (channel_name) => {
	try {
		const { data, error: downloadError } = await chatTable('chats')
			.select(GET_COL_SET)
			.eq('channel_name', channel_name)
			.order('created_at', { ascending: true });

		if (downloadError) {
			throw downloadError;
		}

		return { data, error: null };
	} catch (error) {
		return { data: null, error }; // Handle errors appropriately
	}
};

/**
 * setMsg: Adds a new chat message to the chats table
 * @param message <string> The message content
 * @param author_username <string> The username associated with the message
 * @returns <PostgrestFilterBuilder> | <boolean>false if error.
 *
 * TODO: better return object error handling
 */
export const setMsg = async (
	message: string,
	author_username: string,
	replied_to_id: string | null,
	replied_to_message: string | null,
	replied_to_username: string | null,
	created_by: string | null,
	image_url: string | null,
	channel_name: string | null
) => {
	try {
		const valid =
			typeof message === 'string' && message.trim() !== '' && typeof author_username === 'string';

		if (!valid) {
			throw new Error('Invalid message or username');
		}

		const CLEAN_MESSAGE = DOMPurify.sanitize(message);
		const CLEAN_USERNAME = DOMPurify.sanitize(author_username);

		const retVal = await chatTable('chats')
			.insert([
				{
					message: CLEAN_MESSAGE,
					author_username: CLEAN_USERNAME,
					replied_to_id: replied_to_id,
					replied_to_message: replied_to_message,
					replied_to_username: replied_to_username,
					created_by: created_by,
					image_url: image_url,
					channel_name: channel_name
				}
			])
			.select();

		// console.log('Response from Supabase:', retVal);

		return retVal;
	} catch (error) {
		console.error('Error adding chat message:', error);
		throw new Error('Failed to add chat message');
	}
};

export const addNotification = async (
	message: string,
	author_username: string,
	mentioned: string[],
	replied_to_username: string | null,
	read: boolean,
	post_or_chat_id: string | null,
	created_by: string,
	created_at: Date,
	type: string,
	medium: string,
	post_slug: string,
	channel_name: string
) => {
	console.log('addNotification called with:', { message, mentioned });
	try {
		// console.log('api ran');
		const retVal = await notificationTable('notifications')
			.insert([
				{
					message: message,
					replied_to_username: replied_to_username,
					mentioned: mentioned,
					author_username: author_username,
					read: read,
					post_or_chat_id: post_or_chat_id,
					created_by: created_by,
					created_at: created_at,
					type: type,
					medium: medium,
					post_slug: post_slug,
					channel_name: channel_name
				}
			])
			.select();

		// console.log('addNotification Response from Supabase:', retVal);

		return retVal;
	} catch (error) {
		console.error('Error adding notification:', error);
		throw new Error('Failed to add notification');
	}
};

/**
 * getNotifications - Retrieves notifications for a specific user based on their username.
 *
 * @param {string} author_username - The username of the logged-in user.
 * @returns {Promise<{ data: Chat[]; error: Error | null }>} - A promise containing the retrieved data (list of supabase chat table objects) and any potential error.
 *
 * TODO: Better validation of username and return object.
 */
export const getNotifications = async (loggedInUsername: string) => {
	// console.log('getNotifications ran');
	try {
		const { data, error } = await chatTable('chats')
			.select('*') // Select all columns
			.contains('notifications', [{ mentioned: loggedInUsername }]);

		if (error) {
			console.error('An error occurred:', error);
			return false;
		}

		return { data: data || [], error: null };
	} catch (error) {
		console.error('Error fetching notifications:', error.message);
		return { data: [], error };
	}
};

/**
 * subscribeToChatNotifications - Subscribes to real-time chat notifications for a specific user based on their username.
 *
 * @param {string} author_username - The username of the logged-in user.
 * @param {(notification) => void} callback - The callback function to be executed when a new chat notification is received.
 * @returns {() => void} - A function to unsubscribe from the real-time notifications.
 *
 * TODO: Better validation of username and callback function.
 */
export const subscribeToChatNotifications = (
	author_username: string,
	// eslint-disable-next-line no-unused-vars
	callback: (notification: any) => void
) => {
	// console.log('subscribeToChatNotifications ran');
	const channel = supabaseClient
		.channel('schema-db-changes')
		.on(
			'postgres_changes',
			{
				event: 'INSERT',
				schema: 'public',
				table: 'chats'
			},
			(payload) => {
				const newChat = payload.new;

				// Check if the message contains the username
				// TODO: it would crawl less data to only look for author_username in the notifications column
				if (newChat.message && newChat.message.includes(`@${author_username}`)) {
					callback(newChat);
				}
			}
		)
		.subscribe();

	return () => channel.unsubscribe();
};

// TODO: for this to work, we need to be able use auth in the policy in chats. So... we can't yet delete or edit chats until we figure that out
/**
 * deleteChat ; deletes chat message with matching id
 *
 * @param id <string> The UUID of the post to delete
 * @returns <PostgrestFilterBuilder> object of the Supabase PostgreSQL query, with error details if applicable
 *
 */
export const deleteChat = async (id: string, created_by: string) => {
	let _data, _error;
	// make sure id is a uuid
	try {
		uuidValidate(created_by);
		// delete post with matching id. created_by must match user().id due to policy
		await chatTable('chats').delete().match({ id });
		// console.log('Message deleted successfully');
	} catch (error) {
		console.error('Error deleting message:', error);
	}
	// this is a trick to create a <PostgrestFilterBuilder> object if there's no matching record
	const data = _data;
	const error = _error;
	return { data, error };
};

/**
 *
 * @param file The picture we're saving to storage
 * @returns <PostgrestFilterBuilder> | <boolean>false if error.
 *
 * TODO: better validation on the file contents/size/dimensions/other meta
 *       better return object error handling
 */
export const setChatImage = async (file: File, chat: ChatAttrs) => {
	const valid = uuidValidate(chat.created_by);
	if (valid) {
		// const owner = chatOwner.subscribe((ret) => {
		// 	return ret;
		// });
		// if (owner) {
		// console.log('updateImage: IS owner');
		const user = await getUser();
		const fileExt = file.name.split('.').pop();
		const fileName = `${user.id}${Math.random()}.${fileExt}`;
		const filePath = `${fileName}`;

		const { error: uploadError } = await chatBucket('images').upload(filePath, file);
		if (uploadError) {
			throw uploadError;
		}

		// return await chatTable('chats').upsert({ id: chat.id, image_url: filePath });
		const retVal = chatTable('chats')
			.update({ image_url: filePath })
			.match({ id: chat.id })
			.select();

		// console.log(retVal);
		return retVal;
		// } else {
		// 	// console.log('updateImage: NOT owner');
		// 	return {
		// 		data: '',
		// 		error: 'updateImage: NOT owner'
		// 	};
	}
};

/**
 *
 * @param url <string> - the Supabase URL string of the image to fetch
 * @returns <string> - a URL object string that can be used in an <img src={getImageSm('someImage')}> tag
 *
 * TODO: string validation
 *       better return object error handling
 */
export const getChatImage = async (url: string) => {
	const { data, error: downloadError } = await chatBucket('chats').download(url);
	if (downloadError) {
		throw downloadError;
	}
	return URL.createObjectURL(data);
};

/**
 * getReadStatusForUser - Gets the 'read' status for a given mentionedUsername in a specific chat
 *
 * @param {string} id - The ID of the chat
 * @param {string} mentionedUsername - The username mentioned in the chat
 * @returns {boolean | null} - The 'read' status for the mentionedUsername in the specified chat, or null if not found
 *
 * TODO: better validation of ID and return object
 */
export const getReadStatusForUser = async (id: string, profileUsername: string) => {
	try {
		// Fetch the chat with the given ID
		const { data, error } = await chatTable('chats').select('notifications').eq('id', id).single();

		if (error) {
			console.error('Supabase error:', error.message);
			console.error('An error occurred:', error);
			return false;
		}

		// Extract the notifications array from the chat data
		const notifications = data?.notifications || [];

		// Find the notification for the specified mentionedUsername
		const notification = notifications.find((n) => n.mentionedUsername === profileUsername);

		// If the notification is found, return its 'read' status, otherwise return null
		return notification?.read ?? null;
	} catch (error) {
		console.error('Error fetching chat:', error.message);
		return false;
	}
};

/**
 * markAllNotificationAsRead - Marks the notification for a given mentionedUsername in a specific chat as read
 *
 * @param {string} id - The ID of the chat
 * @param {string} profileUsername - The username of the profile for which the notification is marked as read
 * @returns {boolean} - Returns true if the operation is successful, false otherwise
 *
 * TODO: better validation of ID and input parameters
 */
export const markAllNotificationAsRead = async (profileUsername: string) => {
	try {
		// Fetch only the notifications column for chats containing notifications mentioning the specified profileUsername
		const { data: notificationsData, error: notificationsError } = await chatTable('chats')
			.select('notifications')
			.contains('notifications', [{ mentionedUser: profileUsername }])
			.single();

		if (notificationsError) {
			console.error('Supabase error:', notificationsError.message);
			throw notificationsError;
		}

		// Iterate through each chat and mark the corresponding notification as read
		for (const { notifications, id: chatId } of notificationsData) {
			// Update notifications array, marking all matching notifications as read
			const updatedNotifications = notifications.map((notification) => ({
				...notification,
				read: notification.mentionedUser === profileUsername ? true : notification.read
			}));

			// Update the chat with the modified notifications array
			await chatTable('chats')
				.update({
					notifications: updatedNotifications
				})
				.eq('id', chatId);
		}

		return true;
	} catch (error) {
		console.error('Error marking notifications as read:', error.message);
		return false;
	}
};

export const getNumNewNotifications = async (profileUsername: string) => {
	try {
		// Fetch the count of unread notifications for the specified profileUsername
		const { data, error } = await supabaseClient
			.from('chats')
			.select('*')
			.contains('notifications', [{ mentioned: 'magenta', read: false }]);

		console.log('Data:', data);
		console.error('Error:', error);
		// Log the entire response to inspect its structure
		console.log('Supabase response:', { data, error });

		if (data && data.length > 0) {
			// Notifications exist, handle accordingly
			// Update the state or return the count as needed
			// If you need to update state, use profiles.update((profile) => { /* update logic */ });

			// For now, let's return an empty value
			return null;
		} else if (error) {
			console.error('Supabase error:', error.message);
			return false;
		} else {
			// Notifications do not exist
			console.log('No notifications found for', profileUsername);
			return null;
		}
	} catch (error) {
		console.error('Error fetching notifications:', error.message);
		return false;
	}
};
